// session-runner-usage-extraction.ts
//
// Split out of session-runner-stream-consumer.ts (phase 04, D12 — trần dòng,
// file was exactly 200 lines): pure move of the usage/cost extraction
// helpers. No behavior change — same reads, same field mapping.
import type { ParsedEvent } from "./stream-json-event-parser.js";

export interface SessionUsage {
  inputTokens: number | undefined;
  outputTokens: number | undefined;
  /** Provider-reported cost (never computed from a per-token price we'd have to invent). */
  costUsd: number | undefined;
}

function numOrUndefined(value: unknown): number | undefined {
  return typeof value === "number" && Number.isFinite(value) ? value : undefined;
}

/**
 * Reads `usage.input_tokens`/`usage.output_tokens`/`total_cost_usd` straight
 * off the raw `result` event's passthrough fields (verified present on
 * Claude Code 2.1.x's real result event via
 * test/fixtures/autopilot/stream/prose-json-result.jsonl and happy-path's
 * total_cost_usd — ResultEventSchema in stream-json-event-parser.ts is
 * `.passthrough()`, so these survive the zod parse untyped). Returns
 * undefined entirely when there was no single result event to read from;
 * per-field undefined when the event exists but the worker didn't report
 * that particular figure -- P13 mission-cost-accounting.ts's
 * recordAttemptUsage already tolerates all-undefined input.
 */
export function extractUsage(
  raw: Extract<ParsedEvent, { kind: "result" }>["raw"] | undefined,
): SessionUsage | undefined {
  if (raw === undefined) return undefined;
  const usageObj = (raw as { usage?: unknown }).usage;
  const usage =
    usageObj !== null && typeof usageObj === "object"
      ? (usageObj as Record<string, unknown>)
      : undefined;
  return {
    inputTokens: numOrUndefined(usage?.input_tokens),
    outputTokens: numOrUndefined(usage?.output_tokens),
    costUsd: numOrUndefined((raw as { total_cost_usd?: unknown }).total_cost_usd),
  };
}
